import { Loading, Section } from "@dgs/core";
import { useQuery } from "@tanstack/react-query";
import { Navigate, Route, Routes, useParams } from "react-router";
import styled from "styled-components";
import { EventUsersList } from "../../../../shared/events/eventUsers/EventUsersList";
import { EventFeedbackEvaluation } from "../../../../shared/events/feedback/EventFeedbackEvaluation";
import { EventOffboardingPage } from "../../../../shared/events/offboarding/EventOffboardingPage";
import { eventService } from "../../shared/api/eventService";
import { BackLink } from "../../shared/ui/Back";
import { EventDetailsCard } from "./EventDetailsCard";
import { EventDetailsContent } from "./EventDetailsContent";
import { EventDetailsHeadline } from "./EventDetailsHeadline";
import { EventFeedback } from "./EventFeedback";
import { EventImage } from "./EventImage";
import { EventOverviewCard } from "./EventOverviewCard";
import { EventSpeakersCard } from "./EventSpeakersCard";

const LoadingContent = styled(Section).attrs({ color: "content" })`
	display: flex;
	justify-content: center;
	align-items: center;
	height: 800px;
`;

const ContentSection = styled(Section).attrs({ color: "content" })`
	display: grid;
	grid-template-columns: 1fr;
	gap: ${({ theme }) => theme.spacing(4)};
	padding-top: ${({ theme }) => theme.spacing(6)};
	padding-bottom: ${({ theme }) => theme.spacing(6)};
	align-items: start;
`;

const TwoColumnSection = styled(ContentSection).attrs({ color: "content" })`
	${({ theme }) => theme.breakpoints.tablet} {
		grid-template-columns: 400px 1fr;
	}
`;

const OffboardingSection = styled(Section).attrs({ color: "content" })`
	padding-top: ${({ theme }) => theme.spacing(6)};
	padding-bottom: ${({ theme }) => theme.spacing(6)};
`;

export const EventDetailsPage = () => {
	const { viewType = "", eventId = "" } = useParams<{ viewType: string; eventId: string }>();
	const { data: event, isLoading } = useQuery({
		queryKey: eventService.detailsKey(eventId),
		queryFn: () => eventService.getDetails(eventId).then((r) => r.data.data),
	});

	if (isLoading || !event) {
		return (
			<LoadingContent>
				<Loading />
			</LoadingContent>
		);
	}

	return (
		<Routes>
			<Route
				path="feedback"
				element={
					<ContentSection>
						<BackLink to=".." />
						<EventImage image={event.image} />
						<EventFeedback eventId={event.id} />
					</ContentSection>
				}
			/>
			<Route
				path="*"
				element={
					<TwoColumnSection>
						<BackLink to={`/events/${viewType}`} />
						<EventDetailsHeadline event={event} />
						<EventImage image={event.image} />
						<EventOverviewCard event={event} />
						<EventDetailsContent event={event} />
					</TwoColumnSection>
				}
			>
				<Route index element={<Navigate to="details" replace />} />
				<Route
					path="details"
					element={
						<>
							<EventDetailsCard event={event} />
							{!!event.speakers.length && <EventSpeakersCard speakers={event.speakers} />}
						</>
					}
				/>
				<Route
					path="users"
					element={
						<EventUsersList
							isEditableByUser={event.hasProjectManagerPermissions}
							keysToInvalidate={[eventService.detailsKey(eventId)]}
						/>
					}
				/>
				<Route path="feedback-evaluation" element={<EventFeedbackEvaluation eventId={event.id} />} />
			</Route>
			<Route path="offboarding">
				<Route index element={<Navigate to="key-metrics" replace={true} />} />
				<Route
					path="*"
					element={
						<OffboardingSection>
							<EventOffboardingPage variant="outlined" />
						</OffboardingSection>
					}
				/>
			</Route>
		</Routes>
	);
};
